---
id: pipeline-optimization
title: Pipeline Speed Optimization
category: cicd
difficulty: advanced
tags:
  - pipeline
  - optimization
  - performance
  - caching
  - parallelism
description: >
  Reviews pipeline configuration and suggests optimizations for faster
  build times, better caching, and parallelism.
whenToUse: >
  When your CI/CD pipeline is slow and you want data-driven suggestions
  to reduce build time.
variables:
  - name: pipeline_config
    desc: Full pipeline YAML configuration
  - name: avg_duration
    desc: Average pipeline run duration
  - name: p95_duration
    desc: 95th percentile duration
  - name: cache_hit_rate
    desc: Current cache hit percentage
  - name: max_parallel
    desc: Maximum parallel jobs allowed
  - name: runner_type
    desc: Runner type (self-hosted, cloud, etc.)
tips:
  - Include actual timing data from recent runs for more accurate analysis.
  - Mention any build matrix or fan-out patterns already in use.
  - Specify cost constraints — some optimizations trade cost for speed.
---

You are a CI/CD performance engineer. Analyze this pipeline configuration and suggest optimizations.

## Pipeline Configuration
```yaml
{{pipeline_config}}
```

## Current Metrics
- Average duration: {{avg_duration}}
- P95 duration: {{p95_duration}}
- Cache hit rate: {{cache_hit_rate}}

## Constraints
- Max parallel jobs: {{max_parallel}}
- Runner type: {{runner_type}}

## Task
Analyze the pipeline and provide optimization recommendations:
1. Identify bottlenecks (longest stages, sequential steps that could parallelize)
2. Suggest caching improvements
3. Recommend parallelization opportunities
4. Estimate time savings for each suggestion

## Output Format (JSON)
{
  "bottlenecks": [
    { "stage": "name", "duration_pct": 0, "issue": "description" }
  ],
  "optimizations": [
    {
      "title": "string",
      "category": "caching | parallelism | config | dependency | artifact",
      "impact": "high | medium | low",
      "estimated_savings": "string (e.g. '2-3 min')",
      "implementation": "string — how to implement",
      "tradeoffs": "string — any downsides"
    }
  ],
  "estimated_total_savings": "string",
  "optimized_config_snippet": "string — key changes in YAML"
}

<!-- EXAMPLE_OUTPUT -->

{
  "bottlenecks": [
    { "stage": "npm install", "duration_pct": 35, "issue": "No dependency caching configured" },
    { "stage": "e2e tests", "duration_pct": 40, "issue": "Running sequentially, could split by test suite" }
  ],
  "optimizations": [
    {
      "title": "Add npm dependency caching",
      "category": "caching",
      "impact": "high",
      "estimated_savings": "2-4 min",
      "implementation": "Add actions/cache with package-lock.json hash key",
      "tradeoffs": "None significant — cache invalidates on lockfile change"
    }
  ],
  "estimated_total_savings": "5-8 min per run",
  "optimized_config_snippet": "..."
}
